import React from 'react'
import {
  FileText,
  FileSearch,
  Brain,
  Layers3,
  FileStack,
  Save,
  CheckCircle2,
  Check,
  type LucideIcon
} from 'lucide-react'
import AIAgentOrbLoader from '../../../AIAgentOrb/AIAgentOrbLoader.tsx'
import orbStyles from '../../../AIAgentOrb/AIAgentOrbLoader.module.scss'
import { formatDocumentationPipelineFileSize } from '../../../../../services/projectAutofillDraftService.ts'
import styles from './DocumentPipelineLoopLoader.module.scss'

const RUBRO_DOCUMENTATION_GENERATION_PHASE = 'generating_rubro_documentation'

const RUBRO_DOCUMENTATION_SUBPHASES = [
  'suggesting_technical_specifications',
  'assigning_metric_sheets',
  'suggesting_quotation_sheets'
] as const

const RUBRO_DOCUMENTATION_SUBPHASE_LABELS: Record<string, string> = {
  suggesting_technical_specifications: 'Especificaciones técnicas sugeridas',
  assigning_metric_sheets: 'Cómputos métricos',
  suggesting_quotation_sheets: 'Planillas de cotización'
}

const RUBRO_DOCUMENTATION_LEGACY_PHASES = new Set([
  'categorizing',
  'generating_quotations',
  'suggesting_budget_sheets'
])

const UNIFIED_PIPELINE_STEPS: { phase: string; label: string; Icon: LucideIcon }[] = [
  { phase: 'draft_ready', label: 'Borrador listo', Icon: FileText },
  { phase: 'extracting_text', label: 'Extrayendo texto del PDF', Icon: FileSearch },
  { phase: 'analyzing_document', label: 'Analizando el documento', Icon: Brain },
  { phase: 'matching_skills', label: 'Alineando rubros al catálogo', Icon: Layers3 },
  {
    phase: RUBRO_DOCUMENTATION_GENERATION_PHASE,
    label: 'Generando documentación',
    Icon: FileStack
  },
  { phase: 'saving_draft', label: 'Guardando borrador…', Icon: Save },
  { phase: 'finalizing', label: 'Finalizando', Icon: CheckCircle2 }
]

const PIPELINE_PHASE_LABELS_ES: Record<string, string> = {
  draft_ready: 'Borrador listo',
  extracting_text: 'Extrayendo texto del PDF',
  analyzing_document: 'Analizando el documento',
  matching_skills: 'Alineando rubros al catálogo',
  suggesting_technical_specifications: 'Especificaciones técnicas sugeridas',
  assigning_metric_sheets: 'Cómputos métricos',
  categorizing: 'Clasificando documentación',
  generating_quotations: 'Planillas de cotización',
  suggesting_quotation_sheets: 'Planillas de cotización',
  suggesting_budget_sheets: 'Planillas de presupuesto',
  saving_draft: 'Guardando borrador',
  finalizing: 'Finalizando',
  processing: 'Procesando',
  awaiting_analysis: 'En cola de análisis',
  ready: 'Listo',
  failed: 'Error en el proceso',
  idle: 'En espera',
  [RUBRO_DOCUMENTATION_GENERATION_PHASE]: 'Generando documentación'
}

const isRubroDocumentationSubphase = (
  phase: string | null
): phase is (typeof RUBRO_DOCUMENTATION_SUBPHASES)[number] =>
  phase != null &&
  (RUBRO_DOCUMENTATION_SUBPHASES as readonly string[]).includes(phase)

const mapsToRubroDocumentationStep = (phase: string | null): boolean =>
  isRubroDocumentationSubphase(phase) ||
  (phase != null && RUBRO_DOCUMENTATION_LEGACY_PHASES.has(phase))

const mapToDisplayPhase = (phase: string | null): string | null => {
  if (!phase) return null
  if (mapsToRubroDocumentationStep(phase)) return RUBRO_DOCUMENTATION_GENERATION_PHASE
  return phase
}

const rubroDocumentationDetailLabel = (phase: string): string => {
  if (RUBRO_DOCUMENTATION_SUBPHASE_LABELS[phase]) {
    return RUBRO_DOCUMENTATION_SUBPHASE_LABELS[phase]
  }
  if (phase === 'generating_quotations' || phase === 'suggesting_quotation_sheets') {
    return RUBRO_DOCUMENTATION_SUBPHASE_LABELS.suggesting_quotation_sheets
  }
  if (phase === 'suggesting_budget_sheets') {
    return PIPELINE_PHASE_LABELS_ES.suggesting_budget_sheets
  }
  if (phase === 'categorizing') {
    return PIPELINE_PHASE_LABELS_ES.categorizing
  }
  return phaseLabelEs(phase)
}

const phaseIndex = (displayPhase: string | null, steps: { phase: string }[]): number => {
  if (!displayPhase) return -1
  const i = steps.findIndex((s) => s.phase === displayPhase)
  return i >= 0 ? i : -1
}

const phaseLabelEs = (phase: string): string => {
  const key = phase.trim()
  if (PIPELINE_PHASE_LABELS_ES[key]) return PIPELINE_PHASE_LABELS_ES[key]
  return key
    .split(/_/g)
    .map((w) => {
      const lower = w.toLowerCase()
      if (lower === 'pdf') return 'PDF'
      if (lower === 'id') return 'ID'
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

const UPLOADING_DOCUMENT_TITLE = 'Enviando documento…'

type StreamHint = 'processing' | 'still_working' | null

interface DocumentPipelineLoopLoaderProps {
  pipelinePhase?: string | null
  streamHint?: StreamHint
  primaryFileOriginalName?: string | null
  primaryFileSizeBytes?: number | null
}

const DocumentPipelineLoopLoader: React.FC<DocumentPipelineLoopLoaderProps> = ({
  pipelinePhase = null,
  streamHint = null,
  primaryFileOriginalName = null,
  primaryFileSizeBytes = null
}) => {
  const PIPELINE_STEPS = UNIFIED_PIPELINE_STEPS
  const displayPhase = mapToDisplayPhase(pipelinePhase)
  const activeIdx = phaseIndex(displayPhase, PIPELINE_STEPS)
  const inRubroDocumentation = mapsToRubroDocumentationStep(pipelinePhase)
  const rubroDocDetail =
    pipelinePhase && inRubroDocumentation ? rubroDocumentationDetailLabel(pipelinePhase) : null
  const rubroDocSubIdx = isRubroDocumentationSubphase(pipelinePhase)
    ? RUBRO_DOCUMENTATION_SUBPHASES.indexOf(pipelinePhase)
    : -1
  const unknown = Boolean(pipelinePhase && activeIdx < 0)
  const unknownLabel = unknown && pipelinePhase ? phaseLabelEs(pipelinePhase) : ''
  const total = PIPELINE_STEPS.length
  const stepNum = activeIdx >= 0 ? activeIdx + 1 : null
  const primaryName =
    typeof primaryFileOriginalName === 'string' && primaryFileOriginalName.trim().length > 0
      ? primaryFileOriginalName.trim()
      : null
  const sizeLabel =
    typeof primaryFileSizeBytes === 'number' &&
    primaryFileSizeBytes > 0 &&
    Number.isFinite(primaryFileSizeBytes)
      ? formatDocumentationPipelineFileSize(primaryFileSizeBytes)
      : null
  const indeterminateBar = pipelinePhase === 'extracting_text'
  const progressPct =
    streamHint === 'processing'
      ? 8
      : indeterminateBar
        ? 14
        : activeIdx >= 0
          ? Math.round(((activeIdx + 1) / total) * 100)
          : unknown
            ? 12
            : streamHint === 'still_working'
              ? 10
              : 6

  const stepTitle =
    activeIdx >= 0 ? PIPELINE_STEPS[activeIdx].label : UPLOADING_DOCUMENT_TITLE

  const phaseNamedTitle =
    pipelinePhase === 'extracting_text'
      ? primaryName
        ? `Leyendo ${primaryName}…`
        : 'Leyendo el documento…'
      : pipelinePhase === 'analyzing_document' && primaryName
        ? `Analizando ${primaryName}…`
        : null

  const defaultTitle = unknown
    ? unknownLabel
    : inRubroDocumentation
      ? 'Generando documentación…'
      : phaseNamedTitle ?? (activeIdx >= 0 ? PIPELINE_STEPS[activeIdx].label : UPLOADING_DOCUMENT_TITLE)

  const mainTitle =
    streamHint === 'processing'
      ? 'Procesando…'
      : streamHint === 'still_working'
        ? defaultTitle === UPLOADING_DOCUMENT_TITLE && pipelinePhase
          ? phaseLabelEs(pipelinePhase)
          : defaultTitle
        : defaultTitle

  const eyebrowText =
    primaryName != null
      ? primaryFileSizeBytes != null && primaryFileSizeBytes > 0 && sizeLabel
        ? `Documento · ${sizeLabel}`
        : 'Analizando documento para el borrador'
      : 'Analizando tu PDF para el borrador'

  const showLargePdfHint =
    typeof primaryFileSizeBytes === 'number' && primaryFileSizeBytes >= 3 * 1024 * 1024

  const liveStatus =
    streamHint === 'processing'
      ? 'Procesando.'
      : streamHint === 'still_working'
        ? `${mainTitle}. Sigue en curso.`
        : unknown
          ? `Procesando: ${unknownLabel}`
          : activeIdx >= 0
            ? inRubroDocumentation && rubroDocDetail
              ? `Generando documentación: ${rubroDocDetail}.` +
                (rubroDocSubIdx >= 0
                  ? ` ${rubroDocSubIdx + 1} de ${RUBRO_DOCUMENTATION_SUBPHASES.length}.`
                  : '')
              : `${stepTitle}${primaryName ? ` (${primaryName})` : ''}.` +
                (pipelinePhase === 'extracting_text'
                  ? ' Esta etapa no muestra porcentaje de avance.'
                  : ` Paso ${stepNum} de ${total}.`)
            : 'Enviando documento.'

  const waitingForFirstPhase = activeIdx < 0 && !unknown && streamHint === null

  const progressMetaContent = (() => {
    if (streamHint === 'still_working') {
      return <span className={styles.progressMetaMuted}>Sigue en curso…</span>
    }
    if (streamHint === 'processing') return null
    if (pipelinePhase === 'extracting_text') {
      return (
        <span className={styles.progressMetaMuted}>
          {sizeLabel ? `Tamaño aproximado: ${sizeLabel}. ` : null}
          Esta etapa no indica porcentaje de avance; los archivos grandes pueden tardar varios
          minutos.
        </span>
      )
    }
    if (inRubroDocumentation && rubroDocDetail) {
      return (
        <span className={styles.progressMeta}>
          {rubroDocDetail}…
          {rubroDocSubIdx >= 0 ? (
            <span className={styles.progressMetaHint}>
              {rubroDocSubIdx + 1} de {RUBRO_DOCUMENTATION_SUBPHASES.length} dentro de esta etapa
            </span>
          ) : null}
        </span>
      )
    }
    if (stepNum != null) {
      return (
        <span className={styles.progressMeta}>
          Paso {stepNum} de {total}
          <span className={styles.progressMetaHint}>
            {showLargePdfHint
              ? 'Los PDF grandes pueden tardar varios minutos al extraer texto.'
              : 'Los pasos de análisis pueden tardar varios minutos.'}
          </span>
        </span>
      )
    }
    return <span className={styles.progressMetaMuted}>Conectando con el análisis…</span>
  })()

  return (
    <div
      className={styles.wrap}
      aria-busy="true"
      data-edify-doc-loader="stream-ndjson"
    >
      <div className={styles.shimmerRail} aria-hidden>
        <div className={styles.shimmer} />
      </div>
      <div className={styles.progressBarTrack} aria-hidden>
        <div className={styles.progressBarFill} style={{ width: `${progressPct}%` }} />
      </div>
      <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
        {liveStatus}
      </div>
      <div className={styles.header}>
        <AIAgentOrbLoader status="thinking" className={`${orbStyles.thinkingOrb} ${styles.orb}`} />
        <div className={styles.headerText}>
          <span className={styles.eyebrow}>{eyebrowText}</span>
          <span className={styles.title}>{mainTitle}</span>
          {progressMetaContent}
        </div>
      </div>
      <ol className={styles.steps} aria-label="Etapas del análisis del documento">
        {PIPELINE_STEPS.map(({ phase, label, Icon }, i) => {
          const active = activeIdx >= 0 && i === activeIdx
          const done = activeIdx >= 0 && i < activeIdx
          const waitingSlot = waitingForFirstPhase && i === 0
          const stepClass = `${styles.step} ${active ? styles.stepActive : ''} ${done ? styles.stepDone : ''} ${waitingSlot ? styles.stepWaitingServer : ''}`
          const stepDetail =
            active && phase === RUBRO_DOCUMENTATION_GENERATION_PHASE && rubroDocDetail
              ? `${rubroDocDetail}…`
              : null
          return (
            <li
              key={phase}
              className={stepClass}
              aria-current={active ? 'step' : undefined}
            >
              <span className={styles.stepIcon} aria-hidden>
                {done ? <Check size={18} strokeWidth={2.25} /> : <Icon size={18} strokeWidth={1.85} />}
              </span>
              <span className={styles.stepLabelWrap}>
                <span className={styles.stepLabel}>{label}</span>
                {stepDetail ? <span className={styles.stepSubLabel}>{stepDetail}</span> : null}
              </span>
              {active ? <span className={styles.stepNowBadge}>En curso</span> : null}
              {waitingSlot ? (
                <span className={styles.stepWaitingBadge}>En espera…</span>
              ) : null}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default DocumentPipelineLoopLoader
